//@flow

import {useEffect, useState, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {actions} from './duck';

import {useActions} from '../../../utils/globalStore';
import {
  listScheduledMessagesService,
  createScheduleMessageService,
  editScheduleMessageService,
  deleteScheduleMessageService,
} from './promises';

import type {RootState} from '../..';
import {useCurrentConversation} from '../chat/utils/conversation';
import {getScheduleErrorMessage} from './constants/constants';

import type {
  CreateScheduleMessageRequest,
  EditScheduleMessageRequest,
  ScheduleMessage,
} from '../../../entities';

export function useInitializeScheduleMessage() {
  const setScheduleMessagesAction = useActions(actions.setScheduleMessages);
  useEffect(() => {
    listScheduledMessagesService().then(messages => {
      setScheduleMessagesAction(messages.data);
    });
  }, [setScheduleMessagesAction]);
}

const scheduleMessageSelector = (state: RootState) =>
  state.scheduleMessage.scheduleMessages;

export function useScheduleMessage() {
  const scheduleMessages = useSelector(scheduleMessageSelector);

  return {
    scheduleMessages,
  };
}

const scheduleMessageActions = {
  createScheduleMessagesAction: actions.createScheduleMessages,
  editScheduleMessagesAction: actions.editScheduleMessages,
  deleteScheduleMessagesAction: actions.deleteScheduleMessages,
};

export function useScheduleMessageActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const {
    createScheduleMessagesAction,
    editScheduleMessagesAction,
    deleteScheduleMessagesAction,
  } = useActions(scheduleMessageActions);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError('');
      }, 5000);
    }
  }, [error]);

  const createScheduleMessages = async (
    conversationId: string,
    scheduleMessage: CreateScheduleMessageRequest
  ) => {
    setLoading(true);
    try {
      const message = await createScheduleMessageService(
        conversationId,
        scheduleMessage
      );
      createScheduleMessagesAction(message);
      return message;
    } catch (error) {
      const errorMessage = getScheduleErrorMessage(error);
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const editScheduleMessages = async (
    messageId: string,
    scheduleMessage: EditScheduleMessageRequest
  ) => {
    setLoading(true);
    try {
      const message = await editScheduleMessageService(
        messageId,
        scheduleMessage
      );
      editScheduleMessagesAction(message);
      return message;
    } catch (error) {
      setError('Error al editar el mensaje programado');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteScheduleMessages = async (messageId: string) => {
    setLoading(true);
    try {
      await deleteScheduleMessageService(messageId);
      deleteScheduleMessagesAction(messageId);
    } catch (error) {
      setError('Error al eliminar el mensaje programado');
    } finally {
      setLoading(false);
    }
  };

  return {
    createScheduleMessages,
    editScheduleMessages,
    deleteScheduleMessages,
    loading,
    error,
  };
}

export function useInitializeCurrentConversationScheduleMessage() {
  const {currentConversation} = useCurrentConversation();
  const setScheduleMessagesAction = useActions(
    actions.setCurrentConversationScheduleMessages
  );

  useEffect(() => {
    if (!currentConversation) return;

    listScheduledMessagesService({
      page: 0,
      size: 10,
      crmConversationId: currentConversation.id,
    }).then(messages => {
      setScheduleMessagesAction(messages.data);
    });
  }, [currentConversation, setScheduleMessagesAction]);
}

const currentConversationScheduleMessageSelector = (state: RootState) => {
  const scheduleMessages =
    state.scheduleMessage.currentConversationScheduleMessages || [];
  return [...scheduleMessages].sort((a, b) => b.createdAt - a.createdAt);
};

export function useCurrentConversationScheduleMessage() {
  const scheduleMessages: ScheduleMessage[] = useSelector(
    currentConversationScheduleMessageSelector
  );

  const pendingMessages: ScheduleMessage[] = useMemo(
    () =>
      scheduleMessages?.filter(
        message => message.crmScheduledMessageState === 'PENDING'
      ),
    [scheduleMessages]
  );

  const sentMessages: ScheduleMessage[] = useMemo(
    () =>
      scheduleMessages?.filter(
        message => message.crmScheduledMessageState === 'SENT'
      ),
    [scheduleMessages]
  );

  return {
    scheduleMessages: scheduleMessages || [],
    pendingMessages,
    sentMessages,
  };
}
